<template>
    <router-link
        :to="{ name: 'OrderView', params: { id: action.order_id } }"
        class="card flex gap-x-3 p-3"
    >
        <div class="flex flex-col gap-y-2 pt-0.5">
            <MuvalSwitch
                :vid="`order-action-${action.id}`"
                v-model="isCompleted"
                :loading="mutation.isPending.value"
                size="sm"
                @click.stop.prevent
            />
        </div>
        <div class="flex w-full flex-col gap-y-1">
            <div class="flex flex-col gap-y-1">
                <div class="flex justify-between">
                    <div class="flex items-baseline gap-x-1">
                        <span
                            v-text="action.order.customer?.name"
                            class="font-medium"
                        />
                        <span
                            v-text="'#' + action.order.ref"
                            class="text-muval-gray-3"
                        />
                    </div>
                    <div class="flex items-center gap-x-1">
                        <span
                            v-text="$dayjs(action.schedule_at).format('h:mm A')"
                            class="text-xs whitespace-nowrap"
                            :class="[iconColor]"
                        />
                        <SvgIcon
                            :name="getActionIcon(action.type)"
                            :class="[iconColor, 'size-5 transition-colors']"
                        />
                    </div>
                </div>
            </div>
            <div class="flex gap-x-2 text-sm">
                <span v-text="action.order.move.pickup_locality" />
                <SvgIcon
                    name="system-forward"
                    class="text-muval-gray-3 size-4"
                />
                <span v-text="action.order.move.delivery_locality" />
            </div>
            <div class="mt-1 flex gap-x-1">
                <span
                    v-text="action.note"
                    class="text-muval-gray-2 line-clamp-3 grow"
                />
                <div
                    class="card text-muval-gray-1 flex h-fit items-center gap-1.5 rounded-sm px-2 py-0.5 text-xs whitespace-nowrap"
                >
                    <span
                        :class="getActionStatusDetails.colours"
                        class="size-1.5 rounded-full"
                    />
                    {{ getActionStatusDetails.label }}
                </div>
            </div>
        </div>
    </router-link>
</template>
<script setup lang="ts">
import { useToggleOrderActionCompleted } from '@/composables/useMyOrderActions';
import { capitalize } from '@utilities/filters';

export type MyOrderAction = App.Data.Responses.Me.MyOrderActionData;

const props = defineProps<{
    action: MyOrderAction;
}>();

const mutation = useToggleOrderActionCompleted();

const isCompleted = computed({
    get: () => props.action.completed_at !== null,
    set: (val: boolean) => {
        mutation.mutate({ id: props.action.id, completed: val });
    },
});

const isOverdue = computed(() => {
    if (props.action.completed_at) return false;
    if (!props.action.schedule_at) return false;
    return new Date(props.action.schedule_at).getTime() < Date.now();
});

const iconColor = computed(() => (isOverdue.value ? 'text-error' : 'text-success'));

function getActionIcon(type: MyOrderAction['type']) {
    return {
        CALL: 'system-phone',
        EMAIL: 'system-email-filled',
        SMS: 'system-mobile',
        REMINDER: 'system-notification-filled',
        SITE_INSPECTION: 'system-site-inspection-calendar',
    }[type];
}

const getActionStatusDetails = computed(() => {
    if (!props.action.order.status || !props.action.order.move.status) return;

    const isConfirmed = ['CONFIRMED', 'PARTIALLY_CONFIRMED', 'COMPLETED'].includes(props.action.order.status);

    let label = isConfirmed ? props.action.order.move.status : props.action.order.status;
    label = capitalize(label.replaceAll('_', ' '));

    const { textStyle } = useStatusColours(isConfirmed ? props.action.order.move.status : props.action.order.status);

    return {
        label,
        colours: [textStyle.value.replace('text-', 'bg-')],
    };
});
</script>
